import { useEffect, useRef, useState } from "react";
import { SOLAR } from "@/constants/testIds";
import { searchPlace, type GeocodeResult } from "@/lib/geocode";
import MyLocationButton from "@/components/solar/MyLocationButton";
import { BTN_PRIMARY, ErrorText } from "@/components/solar/ui";

interface Props {
  query: string;
  onQueryChange: (q: string) => void;
  onLocation: (lat: number, lon: number, label: string) => void;
  onDeviceLocation: (lat: number, lon: number) => void;
}

const resultKey = (r: GeocodeResult) => `${r.displayName}|${r.latitude}|${r.longitude}`;

function ResultList({ results, onChoose }: { results: GeocodeResult[]; onChoose: (r: GeocodeResult) => void }) {
  if (results.length < 2) return null;
  return (
    <ul className="mt-6 divide-y divide-[#d9c9ad] border-t border-[#d9c9ad]">
      {results.map((r, i) => (
        <li key={resultKey(r)}>
          <button
            data-testid={`${SOLAR.placeResult}-${i}`}
            onClick={() => onChoose(r)}
            className="flex w-full items-center justify-between py-3 text-left transition hover:text-[#c0501b]"
          >
            <span className="pr-4">{r.displayName}</span>
            <span className="font-sans text-xs tabular-nums text-[#8a6f52]">
              {r.latitude.toFixed(3)}, {r.longitude.toFixed(3)}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function PlaceSearch({ query, onQueryChange, onLocation, onDeviceLocation }: Props) {
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const abortRef = useRef<AbortController | null>(null);

  // Drop any in-flight search if the user switches modes mid-request.
  useEffect(() => () => abortRef.current?.abort(), []);

  const pick = (r: GeocodeResult) => onLocation(r.latitude, r.longitude, r.displayName);

  const run = async () => {
    const q = query.trim();
    if (!q) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setSearching(true);
    setError("");
    try {
      const found = await searchPlace(q, ctrl.signal);
      setResults(found);
      if (found.length) pick(found[0]);
      else setError("No places matched that search.");
    } catch (e: unknown) {
      if (!(e instanceof Error && e.name === "AbortError")) setError("Place search failed.");
    } finally {
      setSearching(false);
    }
  };

  return (
    <>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <input
          data-testid={SOLAR.placeInput}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && run()}
          placeholder="e.g. Kyoto, Japan"
          className="flex-1 border-b border-[#8a6f52] bg-transparent px-1 py-3 font-serif text-xl placeholder-[#a68a6c] outline-none focus:border-[#c0501b]"
        />
        <div className="flex gap-3">
          <button data-testid={SOLAR.placeSearchBtn} onClick={run} disabled={searching} className={BTN_PRIMARY}>
            {searching ? "Searching…" : "Find"}
          </button>
          <MyLocationButton onLocation={onDeviceLocation} onError={setError} />
        </div>
      </div>
      <ErrorText message={error} />
      <ResultList
        results={results}
        onChoose={(r) => {
          pick(r);
          setResults([]);
        }}
      />
    </>
  );
}
